import { useEffect, useId, useRef, useState } from 'react'
/* Un icono suelto, por nombre. Importar el paquete entero traería mil
   quinientos iconos para dibujar uno. */
import { CircleQuestionMark } from 'lucide-react'
import { AYUDA } from './ayuda-contenido'
import type { ClaveDeAyuda } from './ayuda-contenido'

/**
 * El botón «?» que explica un concepto sin sacar a nadie de la pantalla.
 *
 * NO ES UN TOOLTIP DE HOVER, Y ESO NO ES NEGOCIABLE. Un texto que solo aparece
 * al pasar el ratón no existe para quien navega con teclado, para quien usa
 * lector de pantalla ni para quien está en una tableta. Aquí la ayuda es un
 * botón de verdad: se enfoca con Tab, se abre con Enter o espacio, se cierra con
 * Escape y el foco regresa al botón que la abrió.
 *
 * SE USA LA POPOVER API DEL NAVEGADOR CUANDO EXISTE, y no por moda: el globo se
 * pinta en la capa superior, así que no lo recorta el `overflow: auto` de
 * `.tabla-envoltura` —donde vive la matriz de permisos, que es justo donde más
 * falta hace explicar algo—, y el navegador ya implementa cerrar con Escape,
 * cerrar al hacer clic fuera y devolver el foco al disparador. Reimplementar eso
 * a mano es de donde salen los bugs de foco.
 *
 * Y CUANDO NO EXISTE, HAY RESPALDO. Un navegador sin soporte ignora el atributo
 * `popover` y dejaría el globo SIEMPRE visible: no es que la ayuda no funcione,
 * es que la pantalla se llena de cajas. Por eso se detecta el soporte una sola
 * vez y, sin él, el globo se monta y desmonta a mano con el mismo marcado y las
 * mismas garantías de teclado.
 */

/* Se calcula una vez al cargar el módulo: no cambia durante la sesión y así la
   rama elegida es estable entre renders. El `typeof` protege de un entorno sin
   DOM (una prueba en Node, por ejemplo), donde `HTMLElement` no existe. */
const SOPORTA_POPOVER =
  typeof HTMLElement !== 'undefined' && 'popover' in HTMLElement.prototype

/** Separación entre el disparador y el globo, y margen mínimo con el borde. */
const MARGEN = 8

interface PropiedadesAyuda {
  clave: ClaveDeAyuda
  /**
   * Qué explica este botón, para el nombre accesible. Por omisión se usa el
   * título del contenido; se sobrescribe cuando en la pantalla ya hay otro texto
   * que lo dice mejor.
   */
  etiqueta?: string
}

export function Ayuda({ clave, etiqueta }: PropiedadesAyuda) {
  const contenido = AYUDA[clave]
  const idGlobo = useId()
  const refBoton = useRef<HTMLButtonElement | null>(null)
  const refGlobo = useRef<HTMLDivElement | null>(null)
  const [abierto, setAbierto] = useState(false)

  /**
   * El globo se coloca midiendo el disparador, no con posicionamiento por
   * ancla de CSS: el anclaje todavía no está en todos los navegadores, y sin él
   * la Popover API centra el globo en medio de la pantalla, lejos del «?» que se
   * pulsó. Se escribe sobre el estilo del nodo y no en el estado de React porque
   * medir para luego renderizar provocaría un render por cada píxel de scroll.
   */
  function colocar() {
    const boton = refBoton.current
    const globo = refGlobo.current
    if (boton === null || globo === null) return

    const marco = boton.getBoundingClientRect()
    const ancho = globo.offsetWidth
    const alto = globo.offsetHeight

    // Centrado con el disparador, pero sin salirse de la ventana por ningún
    // lado: en una tabla ancha el «?» puede quedar pegado al borde derecho.
    const izquierda = Math.min(
      Math.max(MARGEN, marco.left + marco.width / 2 - ancho / 2),
      Math.max(MARGEN, window.innerWidth - ancho - MARGEN),
    )
    // Debajo si cabe; si no, encima. Un globo que se sale por abajo obliga a
    // desplazarse para leer dos renglones.
    const cabeDebajo = marco.bottom + alto + MARGEN <= window.innerHeight
    const arriba = cabeDebajo
      ? marco.bottom + MARGEN
      : Math.max(MARGEN, marco.top - alto - MARGEN)

    globo.style.left = `${izquierda}px`
    globo.style.top = `${arriba}px`
  }

  /* Con la Popover API el estado real lo lleva el navegador —también lo cierra
     al hacer clic fuera o con Escape—, así que `aria-expanded` se sincroniza
     escuchando su evento en vez de adivinarlo desde el clic. */
  useEffect(() => {
    const globo = refGlobo.current
    if (!SOPORTA_POPOVER || globo === null) return

    function alAlternar() {
      // `:popover-open` en vez de leer `newState` del evento: el tipo
      // `ToggleEvent` no está en todas las versiones de la biblioteca de tipos y
      // el resultado es el mismo.
      setAbierto(globo !== null && globo.matches(':popover-open'))
    }

    globo.addEventListener('toggle', alAlternar)
    return () => globo.removeEventListener('toggle', alAlternar)
  }, [])

  /* Mientras está abierto, el globo sigue al disparador: si la página se
     desplaza o cambia de tamaño, quedarse quieto lo dejaría señalando otra cosa.
     El `true` de la captura hace que también lleguen los desplazamientos de
     contenedores internos, como el de la tabla de permisos. */
  useEffect(() => {
    if (!abierto) return

    colocar()
    window.addEventListener('resize', colocar)
    window.addEventListener('scroll', colocar, true)
    return () => {
      window.removeEventListener('resize', colocar)
      window.removeEventListener('scroll', colocar, true)
    }
    // `colocar` se redefine en cada render pero solo lee refs, así que no hace
    // falta memorizarla: el efecto depende de si está abierto, nada más.
  }, [abierto])

  /* Sin Popover API hay que hacer a mano lo que el navegador regalaba: cerrar
     con Escape devolviendo el foco al disparador, y cerrar al pulsar fuera. Al
     pulsar fuera NO se mueve el foco: ya se fue a donde la persona hizo clic. */
  useEffect(() => {
    if (SOPORTA_POPOVER || !abierto) return

    function alTeclado(evento: KeyboardEvent) {
      if (evento.key !== 'Escape') return
      setAbierto(false)
      refBoton.current?.focus()
    }

    function alPulsar(evento: PointerEvent) {
      const destino = evento.target
      if (!(destino instanceof Node)) return
      if (refGlobo.current?.contains(destino) === true) return
      if (refBoton.current?.contains(destino) === true) return
      setAbierto(false)
    }

    document.addEventListener('keydown', alTeclado)
    document.addEventListener('pointerdown', alPulsar)
    return () => {
      document.removeEventListener('keydown', alTeclado)
      document.removeEventListener('pointerdown', alPulsar)
    }
  }, [abierto])

  const nombre = `Ayuda: ${etiqueta ?? contenido.titulo}`
  /* Con soporte nativo el globo existe siempre en el árbol (el navegador lo
     muestra y lo esconde); sin soporte, solo cuando está abierto. */
  const globoMontado = SOPORTA_POPOVER || abierto

  return (
    <span className="ayuda">
      <ESTILOS />
      <button
        type="button"
        className="ayuda__disparador"
        ref={refBoton}
        aria-label={nombre}
        aria-expanded={abierto}
        // El globo describe al botón: quien usa lector de pantalla oye la
        // explicación al llegar al «?», sin tener que abrirlo para saber qué hay.
        aria-describedby={globoMontado ? idGlobo : undefined}
        popoverTarget={SOPORTA_POPOVER ? idGlobo : undefined}
        onClick={
          SOPORTA_POPOVER
            ? undefined
            : () => setAbierto((anterior) => !anterior)
        }
      >
        {/* El icono sustituye al «?» dibujado con texto, pero NO al nombre
            accesible: ese sigue viniendo del aria-label de arriba, intacto. Por
            eso va aria-hidden — si se anunciara, el botón diría su nombre dos
            veces. */}
        <CircleQuestionMark
          className="ayuda__icono"
          aria-hidden="true"
          focusable="false"
        />
      </button>

      {globoMontado && (
        <div
          className="ayuda__globo"
          id={idGlobo}
          ref={refGlobo}
          popover={SOPORTA_POPOVER ? 'auto' : undefined}
          // No es un diálogo: no atrapa el foco ni bloquea la página. Es una
          // nota, y como nota se anuncia.
          role="note"
        >
          <p className="ayuda__titulo">{contenido.titulo}</p>
          <p className="ayuda__texto">{contenido.texto}</p>
        </div>
      )}
    </span>
  )
}

/**
 * Los estilos del componente viajan con el componente.
 *
 * POR QUÉ NO ESTÁN EN `global.css`: `precedence` hace que React monte una sola
 * copia en el `<head>` por más botones de ayuda que haya en la página, así que
 * el archivo global no crece con cada componente nuevo. No hay ni un color
 * literal: todo sale de `tokens.css`, como manda el proyecto.
 */
function ESTILOS() {
  return (
    <style href="cubiculos-ayuda" precedence="componente">{`
      .ayuda { display: inline-block; line-height: 0; }

      /* El botón se queda sin aro propio porque el icono ya trae el suyo: dos
         circunferencias concéntricas de 24 y 20 píxeles se leen como un error
         de dibujo, no como un control. El borde sigue declarado, transparente,
         para que la caja mida exactamente lo mismo que antes y ninguna fila de
         tabla cambie de alto. */
      .ayuda__disparador {
        font: inherit;
        line-height: 1;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1.5rem;
        height: 1.5rem;
        padding: 0;
        border: 1px solid transparent;
        border-radius: var(--radio-completo);
        background-color: transparent;
        color: var(--texto-tenue);
        cursor: help;
      }

      /* Al apuntarlo se oscurece el trazo y aparece el disco de fondo: es la
         misma respuesta de antes, solo que ahora la da el icono. */
      .ayuda__disparador:hover {
        background-color: var(--superficie-hundida);
        color: var(--texto-fuerte);
      }

      /* Un poco menor que la caja, para que el anillo de foco no salga pegado
         al trazo del icono. El color se hereda del botón por currentColor: aquí
         no hay ni un valor de color propio. */
      .ayuda__icono {
        width: 1rem;
        height: 1rem;
      }

      .ayuda__globo {
        position: fixed;
        /* El agente de usuario centra los popover con márgenes automáticos;
           aquí las coordenadas las calcula el componente. */
        margin: 0;
        inset: auto;
        width: min(22rem, calc(100vw - 2rem));
        max-height: none;
        padding: var(--esp-3) var(--esp-4);
        border: 1px solid var(--borde-normal);
        border-radius: var(--radio-md);
        background-color: var(--superficie-elevada);
        color: var(--texto-normal);
        box-shadow: var(--sombra-md);
        /* Solo cuenta en el respaldo sin Popover API: con soporte nativo el
           globo vive en la capa superior y el z-index es irrelevante. */
        z-index: 30;
      }

      .ayuda__globo::backdrop { background-color: transparent; }

      .ayuda__titulo {
        font-size: var(--texto-sm);
        font-weight: 600;
        color: var(--texto-fuerte);
      }

      .ayuda__texto {
        font-size: var(--texto-sm);
        line-height: 1.5;
      }
    `}</style>
  )
}
